import { memo } from 'react'
import { Handle, Position, type Node } from '@xyflow/react'
import { Mail, Database, FileText, X, Zap, Bell, MessageSquare, Clock, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'

export type ActionType = 'email' | 'webhook' | 'database' | 'document' | 'custom' | 'notification' | 'sms' | 'delay' | 'loop'
export type ActionNodeStatus = 'idle' | 'configured' | 'executing' | 'success' | 'error'

export interface ActionCanvasNodeData {
  label: string
  actionType: ActionType
  status?: ActionNodeStatus
  errorMessage?: string
  emailTo?: string
  emailSubject?: string
  emailTemplate?: string
  webhookUrl?: string
  webhookMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  databaseTable?: string
  databaseOperation?: 'insert' | 'update' | 'delete' | 'query'
  templateId?: string
  templateName?: string
  outputFormat?: 'pdf' | 'docx' | 'html'
  customActionId?: string
  customActionName?: string
  onDelete?: () => void
  [key: string]: unknown
}

export type ActionCanvasNode = Node<ActionCanvasNodeData, 'action'>

const actionTypeIcons: Record<ActionType, React.ComponentType<{ className?: string }>> = {
  email: Mail,
  webhook: Zap,
  database: Database,
  document: FileText,
  custom: Zap,
  notification: Bell,
  sms: MessageSquare,
  delay: Clock,
  loop: RefreshCw,
}

interface ActionCanvasNodeProps {
  data: ActionCanvasNodeData
  selected?: boolean
}

function ActionCanvasNodeComponent({ data, selected }: ActionCanvasNodeProps) {
  const Icon = actionTypeIcons[data.actionType] || Zap

  const getSubtitle = () => {
    switch (data.actionType) {
      case 'email':
        return data.emailTo || 'Send message to contacts'
      case 'webhook':
        return data.webhookUrl ? `${data.webhookMethod || 'POST'} request` : 'Configure API call'
      case 'database':
        return data.databaseTable ? `${data.databaseOperation || 'query'} - ${data.databaseTable}` : 'Database operation'
      case 'document':
        return data.templateName || 'Generate document'
      case 'custom':
        return data.customActionName || 'Custom action'
      case 'notification':
        return 'Send notification'
      case 'sms':
        return 'Send SMS to contacts'
      case 'delay':
        return 'Wait before next step'
      case 'loop':
        return 'Repeat steps'
      default:
        return 'Click to configure'
    }
  }

  return (
    <div
      className={cn(
        'group relative bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 min-w-[200px] max-w-[240px] overflow-hidden',
        'border border-gray-200 dark:border-gray-700',
        selected && 'ring-2 ring-emerald-500 ring-offset-2 dark:ring-offset-gray-900 border-emerald-500'
      )}
    >
      {/* Left color accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-top-1.5"
      />

      {/* Delete button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          data.onDelete?.()
        }}
        className="absolute top-2 right-2 p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 transition-all"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Node Content */}
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start gap-2.5">
          <div className="flex-shrink-0 mt-0.5">
            <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
              <Icon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <div className="min-w-0 flex-1 pr-4">
            <p className="font-medium text-gray-900 dark:text-white text-sm leading-tight">
              {data.label || 'Action'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {getSubtitle()}
            </p>
          </div>
        </div>

        {/* Error message */}
        {data.status === 'error' && data.errorMessage && (
          <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
            <p className="text-xs text-red-600 dark:text-red-400 line-clamp-2">
              {data.errorMessage}
            </p>
          </div>
        )}
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-white dark:!border-gray-800 !-bottom-1.5"
      />
    </div>
  )
}

export default memo(ActionCanvasNodeComponent)
